import { forwardRef, PropsWithChildren } from 'react';

import { IconComponent } from '@repo/ui-icons';
import cn from 'classnames';

import { BaseComponentProps } from '../../../types/common';
import { H5, H6, H7 } from '../../core/Typography';

import styles from './styles.module.css';

export interface DropdownItemSizeProps {
  /**
   * Size of the dropdown item.
   */
  size: 'xs' | 's' | 'm';
}
export interface DropdownItemIconProps {
  /**
   * Icon component to display
   */
  icon: IconComponent;
}

/**
 * Icon component for the `DropdownItem` component.
 */
const DropdownItemIcon: React.FC<DropdownItemIconProps> = ({
  icon: Icon,
  ...rest
}) => {
  return (
    <span {...rest} aria-hidden="true" className={styles['item-icon']}>
      <Icon activeColor="currentColor" color="currentColor" />
    </span>
  );
};

// Get typography component based on size.
export const getLabelComponent = (size: DropdownItemSizeProps['size']) => {
  switch (size) {
    case 'xs':
      return H7;
    case 'm':
      return H5;

    case 's':
    default:
      return H6;
  }
};

export interface DropdownItemProps
  extends PropsWithChildren<BaseComponentProps>,
    DropdownItemSizeProps {
  /**
   * When true, prevents the user from interacting with the item.
   */
  disabled?: boolean;
  /**
   * When true, show styles for active item.
   */
  active?: boolean;
  /**
   * Additional class name for the item.
   */
  className?: string;
}

/**
 * Base component for the `DropdownItem` component.
 */
export const DropdownItemBase = forwardRef<HTMLDivElement, DropdownItemProps>(
  (props, ref) => {
    const {
      disabled,
      active,
      children,
      dataTestId,
      size = 'xs',
      className,
      ...rest
    } = props;

    return (
      <div
        ref={ref}
        className={cn(styles.item, styles[`size-${size}`], className)}
        data-active={active ? '' : undefined}
        data-disabled={disabled ? '' : undefined}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
DropdownItemBase.displayName = 'BasicDropdownItem';

export interface DropdownItemBodyProps extends DropdownItemSizeProps {
  /**
   * Icon component to display on the left side of the label.
   */
  icon?: IconComponent;
  /**
   * Label text to display.
   */
  label: string;
}

/**
 * Left part component for the `DropdownItem` component.
 * Duplicate of the `size` prop is done explicitly to avoid creating context for each component
 * and because item body can be used outside of the dropdown item in Select component.
 */
const DropdownItemBody: React.FC<PropsWithChildren<DropdownItemBodyProps>> = ({
  size,
  children,
  icon,
  label,
}) => {
  const Label = getLabelComponent(size);

  return (
    <div className={cn(styles['item-left-part'], styles[`size-${size}`])}>
      {icon && <DropdownItemIcon icon={icon} />}
      <Label textOverflowEllipsis color="inherit">
        {label}
      </Label>
      {children}
    </div>
  );
};

// Add the Label, Icon and Part properties and cast to DropdownItem
// This creates a component with the expected structure
export const DropdownItem = Object.assign(DropdownItemBase, {
  Body: DropdownItemBody,
  Icon: DropdownItemIcon,
});
